import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../helpers/firebaseContext';

export interface GenevaApiItem {
  CODE?: string;
  LABEL?: string;
  NAMESORT?: string;
  MAKENAME: string;
  DESCRIPTION: string;
  OBJECTDESCRIPTION: string;
  PRICETOLERANCEFLAG?: number;
  PRICETOLERANCEPERCENTAGE?: number;
}

export interface GenevaSelectOption {
  value: string;
  label: string;
}

export const isActiveGenevaLookupItem = (item: GenevaApiItem | null | undefined): item is GenevaApiItem => {
  if (!item) return false;

  return !Object.values(item).some(value =>
    typeof value === 'string' && value.toLowerCase().includes('zzz')
  );
};

// Global cache for API lookup data with timestamps
const lookupCache = new Map<string, { data: GenevaApiItem[], timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Global map to track in-flight requests and deduplicate them
const inFlightRequests = new Map<string, Promise<GenevaApiItem[]>>();

export const useGenevaLookup = (tables: string[] = []) => {
  const [lookupData, setLookupData] = useState<Record<string, GenevaApiItem[]>>({});
  const [loadingLookups, setLoadingLookups] = useState<Record<string, boolean>>({});

  // Fetch Geneva API data for a given table using SELECT *
  const fetchGenevaData = useCallback(async (tableName: string): Promise<GenevaApiItem[]> => {
    // Check cache first
    const cached = lookupCache.get(tableName);
    if (cached) {
      const now = Date.now();
      if (now - cached.timestamp < CACHE_DURATION) {
        return cached.data;
      }
      // Cache expired, remove it
      lookupCache.delete(tableName);
    }

    // Check if there's already an in-flight request for this table
    const existingRequest = inFlightRequests.get(tableName);
    if (existingRequest) {
      console.log(`Reusing in-flight request for table: ${tableName}`);
      return existingRequest;
    }

    // Create new request and store it as in-flight
    const requestPromise = (async () => {
      try {
        // Use Firebase function to get ALL fields via SELECT *
        const getGenevaLookupData = httpsCallable(functions, 'getGenevaLookupData');
        const response = await getGenevaLookupData({ table: tableName });

        const data = response.data as GenevaApiItem[];

        // Cache the result with timestamp
        lookupCache.set(tableName, { data, timestamp: Date.now() });

        return data;
      } catch (error) {
        console.error(`Error fetching Geneva data for ${tableName}:`, error);
        return [];
      } finally {
        // Remove from in-flight requests once complete
        inFlightRequests.delete(tableName);
      }
    })();

    // Store this promise as an in-flight request
    inFlightRequests.set(tableName, requestPromise);

    return requestPromise;
  }, []);

  // Convert API items to Select options
  //should I customize this for UDF ?
  const getSelectOptions = useCallback((tableName: string): GenevaSelectOption[] => {
    const data = lookupData[tableName];
    if (!data) return [];

    const alreadyExists = new Set<string>();

    return data
      .filter(item => {
        if (!isActiveGenevaLookupItem(item)) return false;
        const keyField = item.CODE || item.NAMESORT || item.LABEL;
        if (!keyField || typeof keyField !== 'string' || keyField.trim().length === 0) {
          return false;
        }

        const trimmed = keyField.trim();
        if (alreadyExists.has(trimmed)) {
          return false;
        }

        alreadyExists.add(trimmed);
        return true;
      })
      .map((item) => {
        const keyField = (item.CODE || item.NAMESORT || item.LABEL)!;
        const description = item.DESCRIPTION || item.OBJECTDESCRIPTION || 'No description';
        const trimmedKey = keyField.trim();
        return {
          value: trimmedKey,
          label: `${trimmedKey} - ${description.trim()}`,
        };
      });
  }, [lookupData]);

  // Load lookup data for specified tables
  useEffect(() => {
    tables.forEach(async (tableName) => {
      if (!lookupData[tableName] && !loadingLookups[tableName]) {
        setLoadingLookups(prev => ({ ...prev, [tableName]: true }));

        try {
          const data = await fetchGenevaData(tableName);
          setLookupData(prev => ({ ...prev, [tableName]: data }));
        } finally {
          setLoadingLookups(prev => ({ ...prev, [tableName]: false }));
        }
      }
    });
  }, [tables, fetchGenevaData, lookupData, loadingLookups]);

  return {
    lookupData,
    loadingLookups,
    fetchGenevaData,
    getSelectOptions,
    isLoading: (tableName: string) => loadingLookups[tableName],
    hasData: (tableName: string) => !!lookupData[tableName],
    getOptions: getSelectOptions, // Alias for convenience
  };
}; 
